import React, {useEffect} from "react";
import {
  Box,
  Grid, 
  MenuItem,
  Typography,
} from "@mui/material";
import { useSelector } from 'react-redux'; 
import RegisterStyles from "../../assets/css/register.styles"; 
import { AddCircle, Cancel  } from "@mui/icons-material";
import CustomLabeledTextField from "../../components/customTextField/customLabeledTextField";
import { VALIDATION_ERROR_OPTION_REQUIRED_MESSAGE, VALIDATION_ERROR_REQUIRED_MESSAGE } from "../../constants/actions"; 
import CustomLabeledSelect from "../../components/customSelect/customLabeledSelect";  
import { ValidatableComponent } from "./validatableComponent";
import { InvestorModel } from "../../models/models/investorModel";
import { RegisterTouched } from "./registerTouched";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { RootState } from "../../store/store";
import { LookupEnum } from "../../constants/enums";
import { getBeneficiaryRelationship, getPoliticallyExposedPersonPositions } from "../../reducers/lookupReducer";
import CustomIcon from "../../components/customIcon/customIcon";
import GridStyles from "../../assets/css/grid.styles";
import { InvestorPoliticalPositionFamilyMemberModel } from "../../models/models/investorPoliticalPositionFamilyMemberModel";

const PoliticalPosition: ValidatableComponent = ({
    formData,
    setFormData,
    touched,
    setTouched,
    validateFamilyMembers,
    isRTL,
    t
  }) => {
    const dispatch = useAppDispatch();
    const politicallyExposedPersonPositionsList = useSelector(
      (state: RootState) => state.lookup.politicallyExposedPersonPositions
    );
    const beneficiaryRelationshipList = useSelector(
      (state: RootState) => state.lookup.beneficiaryRelationship
    );
  
    useEffect(() => {
      dispatch(
        getPoliticallyExposedPersonPositions({
          lookupId: LookupEnum.PoliticallyExposedPersonPositions,
        })
      );
      dispatch(
        getBeneficiaryRelationship({ lookupId: LookupEnum.BeneficiaryRelationship })
      );
    }, []); 
  
    const removeFamily = (index: number) => {
      setFormData((prevData) => ({
        ...prevData,
        politicalPosition: {
          ...prevData.politicalPosition,
          familyMembers: prevData.politicalPosition.familyMembers.filter(
            (_, i) => i !== index
          ),
        },
      }));
    };
  
    const handleFamilyMemberFieldChange = (
      index: number,
      field: keyof InvestorPoliticalPositionFamilyMemberModel,
      value: string | number
    ) => {
      validateFamilyMembers()
      setFormData((prevData) => ({
        ...prevData,
        politicalPosition: {
          ...prevData.politicalPosition,
          familyMembers: prevData.politicalPosition.familyMembers.map((family, i) =>
            i === index ? { ...family, [field]: value } : family
          ),
        },
      }));
    };
  
    const addFamily = () => {
      setFormData((prevData) => ({
        ...prevData,
        politicalPosition: {
          ...prevData.politicalPosition,
          familyMembers: [
            ...prevData.politicalPosition.familyMembers,
            {
              id: 0,
              pepPositionId: null,
              name: "",
              relationshipId: null,
              civilIdNumber: null,
            },
          ],
        },
      }));

      setTouched((prevTouched) => ({
        ...prevTouched,
        familyMembers: [
          ...prevTouched.familyMembers || [],
          { 
            name:false, 
            pepPositionId:false, 
            relationshipId:false, 
            civilIdNumber:false, 
          },
        ],
      }));

    };
  
  return (
    <Box
    display="flex"
    flexDirection="column"
    gap={2}
    mb={2}
    pl={5}
    sx={{
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr", 
      marginRight: '50px', 
    }}
  >
        <Box sx={RegisterStyles.boxStyle}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={6} md={6}>
            <CustomLabeledSelect
              label={t('pepPosition')}
              name="pepPositionId"
              isRTL={isRTL}
              type="text"
              SelectProps={{
                children: Array.isArray(politicallyExposedPersonPositionsList.data)
                  ? politicallyExposedPersonPositionsList.data.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {isRTL ? item.valueAr : item.valueEn}
                      </MenuItem>
                    ))
                  : null,
              }}
              value={formData.politicalPosition.pepPositionId || ""}
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  politicalPosition: {
                    ...prevData.politicalPosition,
                    pepPositionId: e.target.value as number,
                  },
                }))
              }
              required={true}
              multiple={false}
              error={
                touched.pepPositionId && formData.politicalPosition.pepPositionId === null
              }
              helperText={
                touched.pepPositionId && formData.politicalPosition.pepPositionId === null
                  ? `${VALIDATION_ERROR_OPTION_REQUIRED_MESSAGE}`
                  : ""
              }
            />
          </Grid>
        </Grid>
        </Box>
        <Box sx={RegisterStyles.boxStyle}>
          <Typography variant="h6" gutterBottom  sx={{ fontWeight: 'bold'}}>
                {t('pepFamilyMembers')}
          </Typography>
          {formData.politicalPosition.familyMembers.map((family, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12} sm={6} md={3}>
                <CustomLabeledTextField
                  label={t('familyMemberName')}
                  isRTL={isRTL}
                  name="name"
                  type="text"
                  required={true}
                  value={family.name}
                  onChange={(e) =>
                    handleFamilyMemberFieldChange(index, "name", e.target.value)
                  }
                  error={touched.familyMembers && touched.familyMembers[index]?.name || false}
                  helperText={
                    touched.familyMembers && touched.familyMembers[index]?.name
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CustomLabeledSelect 
                  label={t('familyMemberPositon')}
                  isRTL={isRTL}
                  name="pepPositionId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(politicallyExposedPersonPositionsList.data)
                      ? politicallyExposedPersonPositionsList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {isRTL ? item.valueAr : item.valueEn}
                          </MenuItem>
                        ))
                      : null,
                  }}
                  value={family.pepPositionId || ""}
                  onChange={(e) =>
                    handleFamilyMemberFieldChange(
                      index,
                      "pepPositionId",
                      Number(e.target.value) || 0
                    )
                  }
                  multiple={false}
                  required={true}
                  error={
                    touched.familyMembers && touched.familyMembers[index]?.pepPositionId || false
                  }
                  helperText={
                    touched.familyMembers && touched.familyMembers[index]?.pepPositionId
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomLabeledSelect 
                  label={t('stateOfRelationShip')}
                  isRTL={isRTL}
                  name="relationshipId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(beneficiaryRelationshipList.data)
                      ? beneficiaryRelationshipList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {isRTL ? item.valueAr : item.valueEn}
                          </MenuItem>
                        ))
                      : null,
                  }}
                  value={family.relationshipId || ""}
                  onChange={(e) =>
                    handleFamilyMemberFieldChange(
                      index,
                      "relationshipId",
                      Number(e.target.value) || 0
                    )
                  }
                  multiple={false}
                  required={true}
                  error={
                    touched.familyMembers && touched.familyMembers[index]?.relationshipId || false
                  }
                  helperText={
                    touched.familyMembers && touched.familyMembers[index]?.relationshipId
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CustomLabeledTextField
                  label={t('familyMemberCivilId')}
                  isRTL={isRTL}
                  name="civilIdNumber"
                  type="text"
                  required={true}
                  numericInput={true}
                  allowDecimal={true}
                  allowNegative={false}
                  value={family.civilIdNumber}
                  onChange={(e) =>
                    handleFamilyMemberFieldChange(index, "civilIdNumber", e.target.value)
                  }
                  error={
                    touched.familyMembers && touched.familyMembers[index]?.civilIdNumber || false
                  }
                  helperText={
                    touched.familyMembers && touched.familyMembers[index]?.civilIdNumber
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              {formData.politicalPosition.familyMembers.length > 1 && (
                <Grid item xs={12} sm={6} md={1} alignSelf="center">
                  <CustomIcon
                    tooltipText="remove"
                    onClick={() => removeFamily(index)}
                    style={GridStyles.deleteIcon}
                  >
                    <Cancel />
                  </CustomIcon>
                </Grid>
              )}
            </Grid>
          ))}
          <CustomIcon
            tooltipText="add"
            onClick={addFamily}
            style={GridStyles.editIcon}
          >
            <AddCircle style={{marginLeft:'5px'}} />  {t('addAnother')}
          </CustomIcon>
        </Box>
      </Box>
    );
  };
  
  PoliticalPosition.validate = (formData: InvestorModel): RegisterTouched => {
    const hasFamilyMemberErrors = formData.politicalPosition.familyMembers.every((family) => {
      return (
        family.name.trim() === "" ||
        family.civilIdNumber === null ||
        family.pepPositionId === null ||
        family.relationshipId === null
      );
    });
    const newTouched = {
      pepPositionId: formData.politicalPosition.pepPositionId === null,
      familyMembersFlag: hasFamilyMemberErrors ? true : false
    };
    return newTouched;
  };
  
  export default PoliticalPosition;
 
